<ng-container *transloco="let t">
  <ld-base-modal
    [title]="todo().isAdditionalDocument ? t('modalsUploadDocumentTitleAdditional') : t('modalsUploadDocumentTitle')"
    [size]="'large'"
    [isLoading]="isUploading()"
    [closeOnBackdropClick]="!isUploading()"
    (closeModal)="onCancel()"
  >
    <div modal-content>
      @if (!todo().isAdditionalDocument) {
        <p class="text-sm text-ld-neutral-500 mt-1">{{ todo().documentName }}</p>
        @if (todo().documentNotes) {
          <p class="text-xs text-ld-neutral-400 mt-1">{{ todo().documentNotes }}</p>
        }
      }

      @if (todo().rejectDetails || todo().agentNotes) {
        <div
          id="upload-document-rejection-notice"
          class="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 mb-4"
          role="alert"
        >
          <div class="flex items-start gap-3">
            <img [src]="errorIcon" alt="Error" class="w-5 h-5 mt-0.5" />
            <div>
              <p class="font-semibold text-red-700 text-sm">{{ t('modalsUploadDocumentDocumentRejected') }}</p>
              <p class="text-red-600 text-sm mt-1">{{ todo().rejectDetails || todo().agentNotes }}</p>
            </div>
          </div>
        </div>
      }

      <div
        id="upload-document-dropzone"
        class="border-2 border-dashed rounded-xl p-8 text-center transition-colors cursor-pointer"
        [class.border-ld-primary]="isDragOver()"
        [class.bg-ld-primary-50]="isDragOver()"
        [class.border-ld-neutral-300]="!isDragOver()"
        [class.hover:border-ld-primary]="!isUploading()"
        [class.hover:bg-ld-primary-50]="!isUploading()"
        [class.opacity-50]="isUploading()"
        [class.cursor-not-allowed]="isUploading()"
        role="button"
        tabindex="0"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        (click)="fileInput.click()"
        (keydown.enter)="fileInput.click()"
      >
        <input
          #fileInput
          id="upload-document-file-input"
          type="file"
          class="hidden"
          multiple
          [accept]="'.' + allowedExtensions().join(',.')"
          [disabled]="isUploading()"
          (change)="onFileSelected($event)"
        />
        <mat-icon class="text-5xl! w-12! h-12! text-ld-neutral-400 mb-3 mx-auto">cloud_upload</mat-icon>
        <p class="font-medium text-ld-neutral mb-1">{{ t('modalsUploadDocumentDragDropText') }}</p>
        <p class="text-sm text-ld-neutral-500">{{ t('modalsUploadDocumentOrClickToBrowse') }}</p>
        <p class="text-xs text-ld-neutral-400 mt-2">
          {{ t('modalsUploadDocumentFileLimit', { max: 10, size: maxFileSizeInMb() }) }}
        </p>
        <p class="text-xs text-ld-neutral-400 mt-1">
          {{ t('modalsUploadDocumentAllowedTypes') }}: {{ allowedExtensions().join(', ') }}
        </p>
      </div>

      @if (validationError()) {
        <div id="upload-document-validation-error" class="mt-3 text-sm text-red-600" role="alert">
          {{ validationError() }}
        </div>
      }

      @if (errorMessage()) {
        <div id="upload-document-api-error" class="mt-3 text-sm text-red-600" role="alert">
          {{ errorMessage() }}
        </div>
      }

      @if (selectedFiles().length > 0) {
        <div id="upload-document-file-list" class="mt-4 space-y-3">
          <p class="text-sm font-medium text-ld-neutral">
            {{ t('modalsUploadDocumentSelectedFiles', { count: selectedFiles().length }) }}
          </p>
          @for (file of selectedFiles(); track $index; let i = $index) {
            <div
              [id]="'upload-document-file-' + i"
              class="bg-ld-neutral-50 rounded-lg overflow-hidden border border-ld-neutral-200"
            >
              <div class="flex items-center gap-2 p-3">
                <div class="flex items-center gap-2 flex-1 min-w-0">
                  <mat-icon class="text-green-600 text-xl! w-5! h-5! shrink-0">check_circle</mat-icon>
                  <span class="text-sm font-medium text-ld-neutral truncate">{{ file.name }}</span>
                  @if (compressingFiles()[i]) {
                    <span class="text-xs text-ld-neutral-400 shrink-0">{{ t('modalsUploadDocumentCompressing') }}</span>
                  } @else {
                    <span class="text-xs text-ld-neutral-400 shrink-0">({{ formatFileSize(file.size) }})</span>
                  }
                </div>

                <div class="flex items-center gap-1 shrink-0">
                  <button
                    [id]="'upload-document-notes-toggle-' + i"
                    type="button"
                    class="font-dm text-sm font-semibold text-ld-link hover:text-ld-link-hover cursor-pointer px-2 py-1"
                    [disabled]="isUploading() || compressingFiles()[i]"
                    (click)="toggleNotesEditing(i); $event.stopPropagation()"
                    [attr.aria-label]="(showEditNotes()[i] ? 'Save notes for ' : 'Add notes for ') + file.name"
                  >
                    {{ showEditNotes()[i] ? t('fileUploadSaveNotes') : t('fileUploadAddNotes') }}
                  </button>

                  @if (isPreviewable(file)) {
                    <button
                      [id]="'upload-document-preview-toggle-' + i"
                      type="button"
                      class="p-1 transition-opacity hover:opacity-70 cursor-pointer"
                      [disabled]="isUploading() || compressingFiles()[i]"
                      (click)="togglePreview(i); $event.stopPropagation()"
                      [attr.aria-label]="(showPreview()[i] ? 'Hide preview for ' : 'Preview ') + file.name"
                    >
                      <img [src]="showPreview()[i] ? eyeSlashIcon : eyeIcon" alt="" class="w-5 h-5" />
                    </button>
                  }

                  <button
                    [id]="'upload-document-remove-' + i"
                    type="button"
                    class="p-1 transition-opacity hover:opacity-70 cursor-pointer"
                    [disabled]="isUploading() || compressingFiles()[i]"
                    (click)="removeFile(i); $event.stopPropagation()"
                    [attr.aria-label]="t('modalsUploadDocumentRemoveFile')"
                  >
                    <img [src]="trashIcon" alt="" class="w-5 h-5" />
                  </button>
                </div>
              </div>

              @if (showPreview()[i] && previewUrls()[i]) {
                <div [id]="'upload-document-preview-' + i" class="px-3 pb-3">
                  @if (file.type.startsWith('image/')) {
                    <img
                      [src]="previewUrls()[i]"
                      [alt]="file.name"
                      class="max-w-full max-h-64 rounded-lg border border-ld-neutral-200"
                    />
                  } @else if (file.type === 'application/pdf') {
                    <iframe
                      [src]="safePreviewUrls()[i]"
                      [title]="file.name"
                      class="w-full h-64 rounded-lg border border-ld-neutral-200"
                    ></iframe>
                  }
                </div>
              }

              @if (showEditNotes()[i]) {
                <div [id]="'upload-document-notes-' + i" class="px-3 pb-3">
                  <textarea
                    [id]="'upload-document-note-' + i"
                    rows="2"
                    class="w-full px-3 py-2 text-sm font-dm border border-ld-neutral-200 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-ld-primary focus:border-transparent"
                    [placeholder]="t('fileUploadAddNotePlaceholder')"
                    [value]="fileNotes()[i] || ''"
                    [disabled]="isUploading() || compressingFiles()[i]"
                    (input)="updateNote(i, $any($event.target).value)"
                    [attr.aria-label]="'Notes for ' + file.name"
                  ></textarea>
                </div>
              }
            </div>
          }
        </div>
      }

      @if (isUploading()) {
        <div id="upload-document-progress" class="mt-4">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-ld-neutral">{{ t('modalsUploadDocumentUploading') }}</span>
            <span class="text-sm text-ld-neutral-500">{{ uploadProgress() }}%</span>
          </div>
          <mat-progress-bar mode="determinate" [value]="uploadProgress()" class="rounded-full" />
        </div>
      }
    </div>
    <div modal-footer>
      <button
        id="upload-document-cancel-button"
        type="button"
        class="btn-outline-ld-purple"
        [disabled]="isUploading()"
        (click)="onCancel()"
      >
        {{ t('commonButtonsCancel') }}
      </button>
      <button
        id="upload-document-submit-button"
        type="button"
        class="btn-primary-ld-purple"
        [disabled]="!canUpload()"
        (click)="onUpload()"
      >
        @if (isUploading()) {
          <mat-spinner diameter="16" class="w-4! h-4! [&_circle]:stroke-white!"></mat-spinner>
          {{ t('modalsUploadDocumentUploading') }}
        } @else {
          {{ t('commonMessagesUpload') }}
        }
      </button>
    </div>
  </ld-base-modal>
</ng-container>
